import React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Loader, Edit, Trash2, CheckCircle, Download, LockKeyhole, Sparkles, PenLine, Pin, X } from 'lucide-react';
import { templateApi, SectionType } from '../services/templateApi';
import { formatDateTime } from '../utils/dateFormat';

export const TemplateDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const { data: template, isLoading, error } = useQuery(
    ['template', id],
    () => templateApi.getTemplate(id!),
    {
      enabled: !!id,
    }
  );

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-medical-600 dark:text-medical-400">Loading template...</span>
        </div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-medical-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">Template not found</h3>
            <p className="text-medical-600 dark:text-medical-400 mb-4">The template you're looking for doesn't exist.</p>
            <Link to="/templates" className="btn-primary">
              Back to Templates
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const sections = templateApi.parseSections(template.sections);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/templates')}
          className="flex items-center gap-2 text-medical-600 dark:text-medical-400 hover:text-medical-900 dark:hover:text-medical-200 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Templates
        </button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="h-8 w-8 text-primary-600" />
              <h1 className="text-3xl font-bold text-medical-900 dark:text-medical-100">{template.name}</h1>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-medical-600 dark:text-medical-400">
              {(template as any).hasStyleReference && (
                <>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 font-medium">
                    <Pin className="h-3.5 w-3.5" />
                    Document template
                  </span>
                </>
              )}
              <span>•</span>
              <span>{template.category}</span>
              <span>•</span>
              <span>Updated {formatDateTime(template.updatedAt)}</span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => navigate(`/templates/${id}/edit`)}
              className="btn-secondary flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => {
                if (confirm('Are you sure you want to delete this template?')) {
                  templateApi.deleteTemplate(id!).then(() => {
                    queryClient.invalidateQueries(['templates']);
                    navigate('/templates');
                  });
                }
              }}
              className="btn-secondary text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Style reference actions */}
      {(template as any).hasStyleReference && (
        <div className="card mb-6 flex items-center gap-4">
          <FileText className="h-8 w-8 text-primary-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-medical-900 dark:text-medical-100">Document template</p>
            <p className="text-xs text-medical-500 dark:text-medical-400">
              Formatting, logos, and static content from the original document will be preserved on export.
            </p>
          </div>
          <button
            onClick={async () => {
              const blob = await templateApi.downloadStyleReference(id!);
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${template.name}.docx`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="btn-secondary flex items-center gap-2 text-sm flex-shrink-0"
          >
            <Download className="h-4 w-4" />
            Download Original
          </button>
          <button
            onClick={async () => {
              if (confirm('Remove the document reference? The template will still work but exports will use default formatting.')) {
                await templateApi.deleteStyleReference(id!);
                window.location.reload();
              }
            }}
            className="p-2 text-medical-400 hover:text-red-500 flex-shrink-0"
            title="Remove document reference"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Description */}
      {template.description && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold text-medical-900 dark:text-medical-100 mb-2">Description</h2>
          <p className="text-medical-700 dark:text-medical-300">{template.description}</p>
        </div>
      )}

      {/* Sections */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-medical-900 dark:text-medical-100">Template Sections</h2>
          <span className="text-sm text-medical-600 dark:text-medical-400">
            {sections.length} section{sections.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="space-y-4">
          {sections.map((section, index) => (
            <div
              key={section.id}
              className="border border-medical-200 dark:border-medical-600 rounded-lg p-4 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-semibold text-sm">
                    {index + 1}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-medical-900 dark:text-medical-100">{section.title}</h3>
                      {section.sectionType === 'static' && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                          <LockKeyhole className="h-3 w-3" />
                          Static
                        </span>
                      )}
                      {section.sectionType === 'variable' && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                          <Sparkles className="h-3 w-3" />
                          AI-Generated
                        </span>
                      )}
                      {section.sectionType === 'placeholder' && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300">
                          <PenLine className="h-3 w-3" />
                          Manual
                        </span>
                      )}
                    </div>
                    {section.required && (
                      <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                        <CheckCircle className="h-3 w-3" />
                        <span>Required</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {section.placeholder && (
                <div className="ml-11 mt-2">
                  <p className="text-sm text-medical-600 dark:text-medical-400 italic">
                    Placeholder: "{section.placeholder}"
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Use Template Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={() => navigate('/drafts')}
          className="btn-primary px-8"
        >
          Use This Template
        </button>
      </div>
    </div>
  );
};
